// src/pages/IndustryApplications/IndustryData.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';

export const useIndustryData = () => {
    const { t } = useTranslation();

    // Helper to safely render bullet points from the JSON arrays
    const renderInfoReason = (translationPath) => {
        const items = t(translationPath, { returnObjects: true });
        if (!Array.isArray(items)) return null;
        
        return (
            <ul style={{ paddingLeft: '14px', margin: 0, listStyleType: 'disc', textAlign: 'left' }}>
                {items.map((item, idx) => (
                    <li 
                        key={idx} 
                        style={{ marginBottom: idx === items.length - 1 ? '0' : '4px' }} 
                        dangerouslySetInnerHTML={{ __html: item }} 
                    />
                ))}
            </ul>
        );
    };

    return [
        {
            id: 'advertising',
            logo: require('./assets/logo/amazon_ads.webp'),
            title: t('industryApplicationsAmazon.data.advertising.title'),
            subtitle: t('industryApplicationsAmazon.data.advertising.subtitle'),
            problem: t('industryApplicationsAmazon.data.advertising.problem'),
            impact: t('industryApplicationsAmazon.data.advertising.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsAmazon.data.advertising.solutions.deepfakePrevention.title'), 
                    text: t('industryApplicationsAmazon.data.advertising.solutions.deepfakePrevention.text'),
                    renderType: 'heatmap',
                    regions: [
                        { id: 'text', box: [0.46, 0.17, 0.95, 0.46], reason: t('industryApplicationsAmazon.data.advertising.solutions.deepfakePrevention.regions.text'), severity: 10, position: 'left' },
                        { id: 'live_badge', box: [0.83, 0.0, 1.0, 0.11], reason: t('industryApplicationsAmazon.data.advertising.solutions.deepfakePrevention.regions.live_badge'), severity: 8, position: 'bottom' },
                        { id: 'face', box: [0.13, 0.04, 0.37, 0.58], reason: t('industryApplicationsAmazon.data.advertising.solutions.deepfakePrevention.regions.face'), severity: 9, position: 'right' }
                    ],
                    infoPoint: {
                        id: 'info', box: [0.935, 0.88, 0.00, 0.00], position: 'top',
                        title: t('industryApplicationsAmazon.data.advertising.solutions.deepfakePrevention.infoTitle'),
                        reason: renderInfoReason('industryApplicationsAmazon.data.advertising.solutions.deepfakePrevention.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsAmazon.data.advertising.solutions.vast.title'), 
                    text: t('industryApplicationsAmazon.data.advertising.solutions.vast.text'),
                    renderType: 'shield',
                    mediaType: 'video',
                    mediaUrl: 'https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/video_unbranded.mp4',
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=6bb4df7b2e06e96a799e3b527acaa3c5ef5bc39b563fb3fd272f75156642005a&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&ctx=a76ccc49e51ae4a06a6cb6ae0f762fe60f216fd56a4d266810ee8475ad282491&context=ai_detection&context=fake_news_detection&regions=true&explanation=true',
                    shieldIcon: require('./assets/application/vetraai_logo_advertising_1.webp'),
                    shieldPosition: 'top-right'
                }
            ]
        },
        {
            id: 'alexa',
            title: t('industryApplicationsAmazon.data.alexa.title'),
            logo: require('./assets/logo/amazon_alexa.webp'), 
            subtitle: t('industryApplicationsAmazon.data.alexa.subtitle'),
            problem: t('industryApplicationsAmazon.data.alexa.problem'),
            impact: t('industryApplicationsAmazon.data.alexa.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsAmazon.data.alexa.solutions.audioForensics.title'), 
                    text: t('industryApplicationsAmazon.data.alexa.solutions.audioForensics.text'),
                    renderType: 'audio-player',
                    mediaImage: require('./assets/application/alexa_audio_bg.webp'), // Provide your background image here
                    mediaUrl: 'https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/audio_alexa.mp3', // Update with your AI voice clip URL
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=15d20ee5b6f9de2578163a07d2f0f01e340e97d509b1b90833c83033356876cb&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&context=ai_detection&context=fake_news_detection&regions=true&explanation=true',
                    shieldIcon: require('./assets/application/vetraai_logo_alexa.webp'),
                    infoPoint: {
                        id: 'info', position: 'right',
                        title: t('industryApplicationsAmazon.data.alexa.solutions.audioForensics.infoTitle'),
                        reason: renderInfoReason('industryApplicationsAmazon.data.alexa.solutions.audioForensics.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsAmazon.data.alexa.solutions.automatedBlocking.title'), 
                    text: t('industryApplicationsAmazon.data.alexa.solutions.automatedBlocking.text'),
                    renderType: 'alexa-pipeline' 
                }
            ]
        },
        {
            id: 'sponsored_brands',
            title: t('industryApplicationsAmazon.data.sponsored_brands.title'),
            logo: require('./assets/logo/amazon_ads.webp'), 
            subtitle: t('industryApplicationsAmazon.data.sponsored_brands.subtitle'),
            problem: t('industryApplicationsAmazon.data.sponsored_brands.problem'),
            impact: t('industryApplicationsAmazon.data.sponsored_brands.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsAmazon.data.sponsored_brands.solutions.heatmaps.title'), 
                    text: t('industryApplicationsAmazon.data.sponsored_brands.solutions.heatmaps.text'),
                    renderType: 'heatmap',
                    mediaImage: require('./assets/application/sponsored_brands_0.webp'), // Load your cropped smartwatch image here
                    regions: [
                        { id: 'watch_face', box: [0.21, 0.46, 0.39, 0.67], reason: t('industryApplicationsAmazon.data.sponsored_brands.solutions.heatmaps.regions.watch_face'), severity: 9, position: 'right' },
                        { id: 'knuckles', box: [0.48, 0.38, 0.72, 0.58], reason: t('industryApplicationsAmazon.data.sponsored_brands.solutions.heatmaps.regions.knuckles'), severity: 7, position: 'top' },
                        { id: 'background_figure', box: [0.84, 0.21, 0.89, 0.41], reason: t('industryApplicationsAmazon.data.sponsored_brands.solutions.heatmaps.regions.background_figure'), severity: 6, position: 'left' }
                    ],
                    infoPoint: {
                        id: 'info', box: [0.935, 0.9, 0.00, 0.00], position: 'top',
                        title: t('industryApplicationsAmazon.data.sponsored_brands.solutions.heatmaps.infoTitle'),
                        reason: renderInfoReason('industryApplicationsAmazon.data.sponsored_brands.solutions.heatmaps.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsAmazon.data.sponsored_brands.solutions.moderationTriage.title'), 
                    text: t('industryApplicationsAmazon.data.sponsored_brands.solutions.moderationTriage.text'),
                    renderType: 'sponsored-brands-pipeline' 
                }
            ]
        },
        {
            id: 'amc',
            title: t('industryApplicationsAmazon.data.amc.title'),
            logo: require('./assets/logo/amazon_amc.webp'), // <-- Make sure to add an AMC logo here!
            subtitle: t('industryApplicationsAmazon.data.amc.subtitle'),
            problem: t('industryApplicationsAmazon.data.amc.problem'),
            impact: t('industryApplicationsAmazon.data.amc.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsAmazon.data.amc.solutions.insight.title'), 
                    text: t('industryApplicationsAmazon.data.amc.solutions.insight.text'),
                    renderType: 'amc-dashboard', 
                    infoPoint: {
                        id: 'info', position: 'top',
                        title: t('industryApplicationsAmazon.data.amc.solutions.insight.infoTitle'),
                        reason: renderInfoReason('industryApplicationsAmazon.data.amc.solutions.insight.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsAmazon.data.amc.solutions.workflow.title'), 
                    text: t('industryApplicationsAmazon.data.amc.solutions.workflow.text'),
                    renderType: 'amc-pipeline' 
                }
            ]
        },
        {
            id: 'amazon_store',
            title: t('industryApplicationsAmazon.data.amazon_store.title'),
            logo: require('./assets/logo/amazon_store.webp'),
            subtitle: t('industryApplicationsAmazon.data.amazon_store.subtitle'),
            problem: t('industryApplicationsAmazon.data.amazon_store.problem'),
            impact: t('industryApplicationsAmazon.data.amazon_store.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsAmazon.data.amazon_store.solutions.heatmaps.title'), 
                    text: t('industryApplicationsAmazon.data.amazon_store.solutions.heatmaps.text'),
                    renderType: 'heatmap',
                    regions: [
                        { id: 'spelling_error', box: [0.725, 0.685, 0.855, 0.735], reason: t('industryApplicationsAmazon.data.amazon_store.solutions.heatmaps.regions.spelling_error'), severity: 9, position: 'top' },
                        { id: 'shipping_label', box: [0.665, 0.275, 0.74, 0.455], reason: t('industryApplicationsAmazon.data.amazon_store.solutions.heatmaps.regions.shipping_label'), severity: 8, position: 'top' },
                        { id: 'packing_tape', box: [0.65, 0.1, 0.99, 0.15], reason: t('industryApplicationsAmazon.data.amazon_store.solutions.heatmaps.regions.packing_tape'), severity: 8, position: 'top' },
                        { id: 'hand_anatomy', box: [0.135, 0.29, 0.245, 0.475], reason: t('industryApplicationsAmazon.data.amazon_store.solutions.heatmaps.regions.hand_anatomy'), severity: 7, position: 'bottom' }
                    ],
                    infoPoint: {
                        id: 'info', box: [0.935, 0.9, 0.00, 0.00], position: 'top',
                        title: t('industryApplicationsAmazon.data.amazon_store.solutions.heatmaps.infoTitle'),
                        reason: renderInfoReason('industryApplicationsAmazon.data.amazon_store.solutions.heatmaps.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsAmazon.data.amazon_store.solutions.apiTriage.title'),
                    text: t('industryApplicationsAmazon.data.amazon_store.solutions.apiTriage.text'),
                    renderType: 'amazon-store-pipeline'
                }
            ]
        },
        {
            id: 'reviews',
            title: t('industryApplicationsAmazon.data.reviews.title'),
            logo: require('./assets/logo/amazon_store.webp'), 
            subtitle: t('industryApplicationsAmazon.data.reviews.subtitle'),
            problem: t('industryApplicationsAmazon.data.reviews.problem'),
            impact: t('industryApplicationsAmazon.data.reviews.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.title'), 
                    text: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.text'),
                    renderType: 'text-highlight', 
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=67f76a57150e89ff204f185b908cc23909e451bbabb10e3bdf45cd0e681fe721&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&context=ai_detection&regions=true&explanation=true', 
                    shieldIcon: require('./assets/application/vetraai_logo_white.webp'),
                    textContent: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.textContent'),
                    regions: [
                        { quote: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.regions', { returnObjects: true })[0]?.quote, reason: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.regions', { returnObjects: true })[0]?.reason, severity: 7 },
                        { quote: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.regions', { returnObjects: true })[1]?.quote, reason: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.regions', { returnObjects: true })[1]?.reason, severity: 8 },
                        { quote: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.regions', { returnObjects: true })[2]?.quote, reason: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.regions', { returnObjects: true })[2]?.reason, severity: 8 },
                        { quote: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.regions', { returnObjects: true })[3]?.quote, reason: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.regions', { returnObjects: true })[3]?.reason, severity: 9 }
                    ],
                    infoPoint: {
                        id: 'info', position: 'right',
                        title: t('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.infoTitle'),
                        reason: renderInfoReason('industryApplicationsAmazon.data.reviews.solutions.semanticDetection.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsAmazon.data.reviews.solutions.apiTriage.title'), 
                    text: t('industryApplicationsAmazon.data.reviews.solutions.apiTriage.text'),
                    renderType: 'reviews-pipeline' 
                }
            ]
        },
        {
            id: 'kdp',
            title: t('industryApplicationsAmazon.data.kdp.title'),
            logo: require('./assets/logo/kdp.webp'), // Make sure this logo exists!
            subtitle: t('industryApplicationsAmazon.data.kdp.subtitle'),
            problem: t('industryApplicationsAmazon.data.kdp.problem'),
            impact: t('industryApplicationsAmazon.data.kdp.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.title'), 
                    text: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.text'),
                    renderType: 'text-highlight', 
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=f400599dbfeaafc98c5b5c2ada3c91ee44c9912144eabd7e55b4d8b15e5b3ca8&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&context=ai_detection&regions=true&explanation=true',
                    shieldIcon: require('./assets/application/vetraai_logo_white.webp'),
                    textContent: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.textContent'),
                    regions: [
                        { quote: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.regions', { returnObjects: true })[0]?.quote, reason: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.regions', { returnObjects: true })[0]?.reason, severity: 9 },
                        { quote: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.regions', { returnObjects: true })[1]?.quote, reason: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.regions', { returnObjects: true })[1]?.reason, severity: 10 },
                        { quote: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.regions', { returnObjects: true })[2]?.quote, reason: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.regions', { returnObjects: true })[2]?.reason, severity: 6 },
                        { quote: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.regions', { returnObjects: true })[3]?.quote, reason: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.regions', { returnObjects: true })[3]?.reason, severity: 5 }
                    ],
                    infoPoint: {
                        id: 'info', position: 'right',
                        title: t('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.infoTitle'),
                        reason: renderInfoReason('industryApplicationsAmazon.data.kdp.solutions.semanticDetection.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsAmazon.data.kdp.solutions.ingestScanning.title'), 
                    text: t('industryApplicationsAmazon.data.kdp.solutions.ingestScanning.text'),
                    renderType: 'kdp-pipeline' // New pipeline name
                }
            ]
        },
        {
            id: 'aws',
            title: t('industryApplicationsAmazon.data.aws.title'),
            logo: require('./assets/logo/amazon_aws.webp'), // <-- Be sure to save an AWS logo here!
            subtitle: t('industryApplicationsAmazon.data.aws.subtitle'),
            problem: t('industryApplicationsAmazon.data.aws.problem'),
            impact: t('industryApplicationsAmazon.data.aws.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsAmazon.data.aws.solutions.compliance.title'), 
                    text: t('industryApplicationsAmazon.data.aws.solutions.compliance.text'),
                    renderType: 'aws-chain-of-custody' 
                },
                { 
                    title: t('industryApplicationsAmazon.data.aws.solutions.marketplace.title'), 
                    text: t('industryApplicationsAmazon.data.aws.solutions.marketplace.text'),
                    renderType: 'aws-pipeline' 
                }
            ]
        },
    ];
};